/**
 * Middleware for the voicemail feature.
 *
 * Listens for WebSocket messages from the VRS server related to voicemail
 * and translates them into Redux actions.
 *
 * Also listens for p2p_target_offline messages to show the voicemail prompt.
 */
import logger from '../recording/logger';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE, SET_ROOM } from '../base/conference/actionTypes';
import { JitsiRecordingConstants } from '../base/lib-jitsi-meet';
import { FEATURES } from '../base/whitelabel/constants';
import { isFeatureEnabled } from '../base/whitelabel/functions';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { getLocalVideoTrack } from '../base/tracks/functions.any';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { RECORDING_SESSION_UPDATED } from '../recording/actionTypes';
import { VOICEMAIL_CAPTURE_FAILED, VOICEMAIL_CAPTURE_REQUESTED, VOICEMAIL_ERROR, VOICEMAIL_RECORDING_CANCELLED, VOICEMAIL_RECORDING_SENT, VOICEMAIL_RECORDING_STARTED } from './actionTypes';
import { usesServerRecorder } from './capture';
import { hasRequestedRecorderStop, noteRecorderStopRequested } from './recorderStop';
import { forgetRecordingSession, isRecordingRoomName, rememberRecordingSession, restoreRecordingSession } from './sessionHandoff';
import { captureFailed, dismissPrompt, loadUnreadCount, updateUnreadCount, recordingStarted, recordingComplete, recordingCancelled, removeMessage, showPrompt } from './actions';
let _listenersFor;
let _recordingAttempt;
let _recordingLimit;
function asRecord(data) {
    return data && typeof data === 'object' ? data : {};
}
function asString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function asNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
MiddlewareRegistry.register(store => next => (action) => {
    // Before the reducers run: the room's recorder session is still known.
    if (action.type === CONFERENCE_WILL_LEAVE) {
        stopStartedRecorder(store, action.conference, 'left the room');
    }
    else if (action.type === VOICEMAIL_RECORDING_CANCELLED) {
        stopStartedRecorder(store, _recordingAttempt?.conference, 'discarded');
    }
    else if (action.type === VOICEMAIL_RECORDING_STARTED && _recordingAttempt
        && action.session?.messageId !== _recordingAttempt.messageId) {
        stopStartedRecorder(store, _recordingAttempt.conference, 'recorded again');
    }
    const ending = action.type === VOICEMAIL_RECORDING_SENT || action.type === VOICEMAIL_RECORDING_CANCELLED
        ? store.getState()['features/voicemail']?.recordingSession?.messageId
        : undefined;
    const result = next(action);
    // The session outlives the page load into its room (sessionHandoff.ts).
    if (action.type === VOICEMAIL_RECORDING_STARTED) {
        const session = store.getState()['features/voicemail']?.recordingSession;
        if (session)
            rememberRecordingSession(session);
    }
    else if (ending) {
        forgetRecordingSession(ending);
    }
    else if (action.type === SET_ROOM) {
        restoreRecordingSessionFor(store, action.room);
    }
    // Once for each queue listener generation: a sign-out removes every queue
    // listener (queueService.disconnect) while the app stays open, so the
    // next sign-in registers them again (Robert, 2026-10-29).
    if (_listenersFor !== queueService.listenerGeneration() && isFeatureEnabled(FEATURES.VOICEMAIL)) {
        _listenersFor = queueService.listenerGeneration();
        initializeVoicemailListeners(store);
    }
    if (action.type === CONFERENCE_JOINED || action.type === TRACK_ADDED || action.type === TRACK_UPDATED
        || action.type === VOICEMAIL_CAPTURE_REQUESTED || action.type === VOICEMAIL_RECORDING_STARTED) {
        maybeStartVoicemailRoomRecording(store, action.conference || store.getState()['features/base/conference']?.conference);
    }
    // The screen gave up waiting for the recorder (useVideoMailTakeProgress).
    const failedAttempt = action.type === VOICEMAIL_CAPTURE_FAILED ? _recordingAttempt : undefined;
    if (failedAttempt && failedAttempt.messageId === action.messageId) {
        resetAttempt(failedAttempt);
    }
    if (action.type === RECORDING_SESSION_UPDATED) {
        noteRecorderSession(store, action.sessionData);
        maybeLimitRecording(store, action.sessionData);
    }
    if (action.type === CONFERENCE_LEFT && _recordingAttempt && _recordingAttempt.conference === action.conference) {
        const voicemailState = store.getState()['features/voicemail'];
        const messageId = _recordingAttempt?.messageId;
        _recordingAttempt = undefined;
        clearRecordingLimit();
        if (voicemailState?.isRecording && voicemailState.recordingSession?.messageId === messageId) {
            store.dispatch(recordingCancelled());
        }
    }
    return result;
});
function activeFileRecordingSessionId(store) {
    const { mode, status } = JitsiRecordingConstants;
    return store.getState()['features/recording']?.sessionDatas.find(session => session.mode === mode.FILE && (session.status === status.ON || session.status === status.PENDING))?.id;
}
/**
 * Nothing was recorded: the next press asks the recorder again.
 */
function resetAttempt(attempt) {
    attempt.started = false;
    attempt.capturing = false;
    attempt.sessionId = undefined;
    clearRecordingLimit();
}
/**
 * The recorder did not start this take. The take and its room stay, and the
 * record control says so and can be pressed again.
 */
function failCapture(store, attempt) {
    resetAttempt(attempt);
    store.dispatch(captureFailed(attempt.messageId));
}
function clearRecordingLimit() {
    if (_recordingLimit) {
        clearTimeout(_recordingLimit);
        _recordingLimit = undefined;
    }
}
/**
 * Ends the server recorder this app started in that room. Nothing else stops
 * it: the recorder otherwise runs until it notices the room is empty, and has
 * no length limit of its own.
 */
function stopStartedRecorder(store, conference, reason) {
    const attempt = _recordingAttempt;
    if (!attempt?.started || !conference || attempt.conference !== conference) {
        return;
    }
    clearRecordingLimit();
    const sessionId = activeFileRecordingSessionId(store);
    if (!sessionId || hasRequestedRecorderStop(sessionId) || typeof conference.stopRecording !== 'function') {
        return;
    }
    noteRecorderStopRequested(sessionId);
    logger.info(`VideoMail recorder stopped: ${reason}`);
    const unconfirmed = () => logger.warn(`VideoMail recorder stop was not confirmed: ${reason}`);
    try {
        void Promise.resolve(conference.stopRecording(sessionId)).catch(unconfirmed);
    }
    catch {
        unconfirmed();
    }
}
/**
 * Follows the recorder this app started. It takes about ten seconds to join
 * before it captures anything, and it can fail or be stopped in that time
 * (owner report 2026-10-08: the take stayed on "Starting recording" with no
 * way on). A recorder that ends before it captured anything recorded nothing,
 * so the take goes back to the record control instead of waiting for a file
 * that will never come.
 */
function noteRecorderSession(store, sessionData) {
    const attempt = _recordingAttempt;
    const { mode, status } = JitsiRecordingConstants;
    if (!attempt?.started || !sessionData?.id || sessionData.mode !== mode.FILE
        || store.getState()['features/base/conference']?.conference !== attempt.conference) {
        return;
    }
    if (sessionData.status === status.PENDING || sessionData.status === status.ON) {
        attempt.sessionId = sessionData.id;
        attempt.capturing = attempt.capturing || sessionData.status === status.ON;
        return;
    }
    // Only this start's own recorder: a session from an earlier press that
    // ends late must not touch the press after it.
    if (sessionData.status !== status.OFF || sessionData.id !== attempt.sessionId) {
        return;
    }
    // The recorder has stopped, so the length limit has nothing left to stop.
    clearRecordingLimit();
    if (!attempt.capturing && !hasRequestedRecorderStop(sessionData.id)) {
        logger.warn('VideoMail recorder ended before it recorded');
        failCapture(store, attempt);
    }
}
/** The take's length limit, counted from when the recorder is really recording. */
function maybeLimitRecording(store, sessionData) {
    const attempt = _recordingAttempt;
    const seconds = store.getState()['features/voicemail']?.recordingSession?.maxDurationSeconds;
    if (_recordingLimit || !attempt?.started || sessionData?.mode !== JitsiRecordingConstants.mode.FILE
        || sessionData?.status !== JitsiRecordingConstants.status.ON
        || store.getState()['features/base/conference']?.conference !== attempt.conference
        || typeof seconds !== 'number' || seconds <= 0) {
        return;
    }
    _recordingLimit = setTimeout(() => {
        _recordingLimit = undefined;
        if (_recordingAttempt === attempt) {
            stopStartedRecorder(store, attempt.conference, 'length limit');
        }
    }, seconds * 1000);
}
/**
 * The page opened a VideoMail recording room without its session (on the
 * web, entering a room loads a new page): take it back from the handoff the
 * page that started it wrote, the room app's or the client portal's.
 */
function restoreRecordingSessionFor(store, room) {
    if (!room || !isRecordingRoomName(room))
        return;
    if (store.getState()['features/voicemail']?.recordingSession?.roomName === room)
        return;
    const session = restoreRecordingSession(room);
    if (session) {
        logger.info('VideoMail recording session restored after the page load');
        store.dispatch(recordingStarted(session));
    }
}
function maybeStartVoicemailRoomRecording(store, conference) {
    const state = store.getState();
    const session = state['features/voicemail']?.recordingSession;
    const roomName = state['features/base/conference']?.room;
    if (!state['features/voicemail']?.isRecording || !session?.messageId || !session.roomName
        || session.roomName !== roomName || !conference
        || conference !== state['features/base/conference'].conference) {
        return;
    }
    if (_recordingAttempt?.conference !== conference || _recordingAttempt?.messageId !== session.messageId) {
        _recordingAttempt = { capturing: false, conference, messageId: session.messageId, start: 0, started: false };
    }
    const attempt = _recordingAttempt;
    const localVideoTrack = getLocalVideoTrack(state['features/base/tracks']);
    // Where the browser can record the take itself, it does, and the
    // server-side recorder is never asked to join. See capture.web.ts.
    if (!usesServerRecorder()) {
        return;
    }
    // The recorder starts when the person presses record, not when their
    // camera happens to become ready: otherwise every take opens with several
    // seconds of someone waiting for the light to come on.
    if (!state['features/voicemail']?.isCaptureRequested) {
        return;
    }
    if (attempt.started || !localVideoTrack?.jitsiTrack || localVideoTrack.muted
        || typeof conference.startRecording !== 'function') {
        return;
    }
    const start = attempt.start + 1;
    const recordingFailed = () => {
        const current = store.getState();
        // A settled vendor promise belongs to the room, session and press
        // that started it.
        if (_recordingAttempt !== attempt || attempt.start !== start || !attempt.started
            || current['features/base/conference'].conference !== conference
            || current['features/voicemail'].recordingSession?.messageId !== session.messageId) {
            return;
        }
        logger.warn('VideoMail recording failed to start');
        failCapture(store, attempt);
    };
    attempt.start = start;
    attempt.capturing = false;
    attempt.sessionId = undefined;
    attempt.started = true;
    try {
        // Narrow the vendor's untyped return locally; do not let it enter app state.
        const result = conference.startRecording({
            mode: JitsiRecordingConstants.mode.FILE,
            appData: JSON.stringify({
                file_recording_metadata: {
                    share: false,
                    videomailKind: session.kind || 'message',
                    voicemailMessageId: session.messageId
                }
            })
        });
        void Promise.resolve(result).catch(recordingFailed);
    }
    catch {
        recordingFailed();
    }
}
function showVoicemailPrompt(store, data) {
    const payload = asRecord(data);
    const calleeName = asString(payload.calleeName);
    const calleePhone = asString(payload.calleePhone);
    const calleeId = asString(payload.calleeId);
    const greetingRecord = asRecord(payload.greeting);
    const greetingId = asString(greetingRecord.id);
    if (payload.voicemailAvailable === true && calleeName && (calleePhone || calleeId)) {
        store.dispatch(showPrompt({
            calleeName,
            calleePhone,
            calleeId,
            deliveryReason: asString(payload.deliveryReason),
            dndMessage: asString(payload.dndMessage),
            reason: asString(payload.reason),
            greeting: greetingId ? {
                durationSeconds: asNumber(greetingRecord.durationSeconds),
                id: greetingId,
                playbackUrl: asString(greetingRecord.playbackUrl),
                status: 'available',
                thumbnailUrl: asString(greetingRecord.thumbnailUrl)
            } : null
        }));
    }
}
/**
 * Wire up WebSocket events from the InterpreterQueueService
 * to Redux dispatches for voicemail.
 */
function initializeVoicemailListeners(store) {
    if (!queueService?.on) {
        return;
    }
    // New voicemail message notification
    queueService.on('voicemail_new_message', (data) => {
        const payload = asRecord(data);
        const count = asNumber(payload.count);
        if (count !== undefined) {
            store.dispatch(updateUnreadCount(count));
        }
        else {
            store.dispatch(loadUnreadCount());
        }
    });
    // Unread count push
    queueService.on('voicemail_unread_count', (data) => {
        const count = asNumber(asRecord(data).count);
        if (count !== undefined) {
            store.dispatch(updateUnreadCount(count));
        }
    });
    // Recording started confirmation
    queueService.on('voicemail_recording_started', (data) => {
        const payload = asRecord(data);
        const messageId = asString(payload.messageId);
        const roomName = asString(payload.roomName);
        if (!messageId || !roomName) {
            return;
        }
        store.dispatch(recordingStarted({
            messageId,
            roomName,
            maxDurationSeconds: asNumber(payload.maxDurationSeconds) || 180
        }));
    });
    // Recording complete
    queueService.on('voicemail_recording_complete', (data) => {
        const payload = asRecord(data);
        const messageId = asString(payload.messageId);
        const durationSeconds = asNumber(payload.durationSeconds);
        const kind = asString(payload.kind) === 'greeting' ? 'greeting' : 'message';
        if (messageId && durationSeconds !== undefined) {
            store.dispatch(recordingComplete(messageId, durationSeconds, kind));
        }
    });
    // Recording cancelled
    queueService.on('voicemail_recording_cancelled', (data) => {
        const messageId = asString(asRecord(data).messageId);
        if (messageId && store.getState()['features/voicemail'].recordingSession?.messageId === messageId) {
            _recordingAttempt = undefined;
            store.dispatch(recordingCancelled());
        }
    });
    // Message deleted
    queueService.on('voicemail_message_deleted', (data) => {
        const messageId = asString(asRecord(data).messageId);
        if (messageId) {
            store.dispatch(removeMessage(messageId));
        }
    });
    // Error
    queueService.on('voicemail_error', (data) => {
        store.dispatch({
            type: VOICEMAIL_ERROR,
            error: asString(asRecord(data).message) || 'Voicemail error'
        });
    });
    // P2P/VRS target unavailable → show voicemail prompt
    queueService.on('p2p_target_offline', (data) => {
        showVoicemailPrompt(store, data);
    });
    queueService.on('p2pTargetDnd', (data) => {
        showVoicemailPrompt(store, { ...asRecord(data), reason: asString(asRecord(data).reason) || 'do_not_disturb' });
    });
    // The person we just failed to reach is calling back: their call wins
    // over the VideoMail offer. Only THEIR call — anyone else ringing in
    // while the prompt is up is handled as busy by the incoming-call sheet.
    queueService.on('p2pIncomingCall', (data) => {
        const voicemailState = store.getState()['features/voicemail'];
        const callerId = asString(asRecord(data).callerId);
        if (voicemailState?.isPromptVisible && callerId && callerId === voicemailState.promptData?.calleeId) {
            store.dispatch(dismissPrompt());
        }
    });
    // Joining any room (answered callback, redial) retires the prompt.
    queueService.on('p2pJoinRoom', () => {
        if (store.getState()['features/voicemail']?.isPromptVisible) {
            store.dispatch(dismissPrompt());
        }
    });
    queueService.on('vrsClientUnavailable', (data) => {
        showVoicemailPrompt(store, data);
    });
    console.log('[Voicemail] Middleware initialized');
}
